import { useListUsers, UserAttributeFilterDto, UserAttributeFilterDtoField, UsersControllerListParams, UsersControllerListSortBy } from "@/app2/shared/data";
import { GridFilterModel, GridSortModel } from "@mui/x-data-grid-pro";
import { useCallback, useEffect, useMemo, useState } from "react";

const SORT_FIELDS: readonly string[] = ["firstname", "lastname", "email", "employeeCode", "jobTitle", "division"];

const FILTERABLE_FIELDS: readonly string[] = [
	"firstname",
	"lastname",
	"email",
	"jobTitle",
	"jobCategory",
	"division",
	"streak",
	"overdue",
	"completedModules",
	"createdAt",
	"employeeCode",
	"customFieldValue1",
	"customFieldValue2",
	"customFieldValue3"
];

interface UseAddRecipientModalProps {
    open: boolean;
	onClose: () => void;
	onSave: (userIds: number[]) => void;
	existingRecipientUserIds: number[];
}

const useAddRecipientModal = ({
    existingRecipientUserIds,
    open,
    onClose,
    onSave
}: UseAddRecipientModalProps) => {
    const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
	const [filterModel, setFilterModel] = useState<UsersControllerListParams>({
		page: 1,
		pageSize: 25,
		sortBy: "lastname",
		sortDirection: "asc"
	});

	const {data, isLoading, isFetched} = useListUsers(filterModel);

	useEffect(() => {
		if (open) {
			setSelectedUserIds([]);
		}
	}, [open]);

	const rows = data?.data ?? [];
	const rowCount = data?.total ?? 0;

	const allSelectedUserIds = Array.from(new Set([...selectedUserIds, ...existingRecipientUserIds]));

	const handleSave = () => {
		if (selectedUserIds.length > 0) {
			onSave(selectedUserIds);
			setSelectedUserIds([]);
		}
	};

	const handleClose = () => {
		setSelectedUserIds([]);
		onClose();
	};

	const onPageChange = useCallback((page: number) => {
		setFilterModel((prev) => ({...prev, page: page + 1}));
	}, []);

	const onPageSizeChange = useCallback((pageSize: number) => {
		setFilterModel((prev) => ({...prev, pageSize, page: 1}));
	}, []);

	const onFilterChange = useCallback((newFilterModel: GridFilterModel) => {
		setFilterModel((prev) => {
			const filters: UserAttributeFilterDto[] = (newFilterModel.items ?? [])
				.filter((item: { columnField?: string; field?: string; value?: unknown; operatorValue?: string; operator?: string }) => {
					const field = item.columnField ?? item.field;
					const hasValue = item.value !== undefined && item.value !== null && String(item.value).trim() !== "";
					const operator = item.operatorValue ?? item.operator ?? "co";
					return field && FILTERABLE_FIELDS.includes(field) && (operator === "pr" || hasValue);
				})
				.map((item: { columnField?: string; field?: string; value?: unknown; operatorValue?: string; operator?: string }) => {
					const field = (item.columnField ?? item.field) as UserAttributeFilterDtoField;
					const operator = (item.operatorValue ?? item.operator ?? "co") as UserAttributeFilterDto["operator"];
					let value: string | undefined;
					if (operator === "pr") {
						value = undefined;
					} else if (item.value instanceof Date) {
						value = item.value.toISOString();
					} else {
						value = String(item.value ?? "");
					}
					return { field, value, operator };
				});

			return {
				...prev,
				page: 1,
				filters: filters.length > 0 ? { operator: newFilterModel.linkOperator, conditions: filters } : undefined
			};
		});
	}, []);

	const onSortModelChange = useCallback((sortModel: GridSortModel) => {
		setFilterModel((prev) => ({
			...prev,
			sortBy:
				sortModel.length > 0 && SORT_FIELDS.includes(sortModel[0].field)
					? (sortModel[0].field as UsersControllerListSortBy)
					: "lastname",
			sortDirection: sortModel.length > 0 ? (sortModel[0].sort as "asc" | "desc") : "asc"
		}));
	}, []);

	const onSelectionModelChange = useCallback(
		(newSelection: number[]) => {
			const filtered = newSelection.filter((id) => !existingRecipientUserIds.includes(id));
			setSelectedUserIds(filtered);
		},
		[existingRecipientUserIds]
	);    


    return {
        constants:{
            allSelectedUserIds
        },
        states:{
            isLoading,
            isFetched,
            rows,
            rowCount,
            filterModel,
            selectedUserIds
        },
        api: {
            handleClose,
            handleSave,
            onPageChange,
            onPageSizeChange,
            onSortModelChange,
            onSelectionModelChange,
            onFilterChange
        }
    }
}

export default useAddRecipientModal;